import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Activity, 
  Database, 
  Cloud, 
  Mail, 
  RefreshCw, 
  CheckCircle, 
  XCircle,
  AlertTriangle,
  Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminStatusPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Email test state
  const [testEmail, setTestEmail] = useState('');
  const [testSubject, setTestSubject] = useState('SHRT Test E-Mail');
  const [testMessage, setTestMessage] = useState('Dies ist eine Test-E-Mail vom SHRT Mitgliederbereich.');
  const [sendingEmail, setSendingEmail] = useState(false);

  const getToken = () => localStorage.getItem('shrt_token');

  const fetchStatus = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/api/admin/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch status:', error);
      toast.error('Status konnte nicht geladen werden');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStatus();
  };

  const handleSendTestEmail = async (e) => {
    e.preventDefault();
    
    if (!testEmail) {
      toast.error('Bitte E-Mail-Adresse eingeben');
      return;
    }
    
    setSendingEmail(true);
    
    try {
      const token = getToken();
      const response = await axios.post(`${API_URL}/api/admin/test-email`, {
        to_email: testEmail,
        subject: testSubject,
        message: testMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      setTestEmail('');
    } catch (error) {
      const message = error.response?.data?.detail || 'E-Mail-Versand fehlgeschlagen';
      toast.error(message);
    } finally {
      setSendingEmail(false);
    }
  };

  // Helper functions for status display
  const getDbStatus = () => status?.database?.connected ? 'online' : 'offline';
  const getMondayStatus = () => status?.monday?.configured ? 'configured' : 'offline';
  const getSmtpStatus = () => status?.smtp?.configured ? 'configured' : 'offline';

  const getStatusIcon = (serviceStatus) => {
    switch (serviceStatus) {
      case 'online':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'offline':
        return <XCircle className="text-red-500" size={20} />;
      case 'configured':
        return <CheckCircle className="text-green-500" size={20} />;
      default:
        return <AlertTriangle className="text-yellow-500" size={20} />;
    }
  };

  const getStatusColor = (serviceStatus) => {
    switch (serviceStatus) {
      case 'online':
        return 'text-green-500';
      case 'offline':
        return 'text-red-500';
      case 'configured':
        return 'text-green-500';
      default:
        return 'text-yellow-500';
    }
  };

  const getStatusMessage = (service) => {
    switch (service) {
      case 'mongodb':
        return status?.database?.connected ? 'Verbunden' : 'Nicht verbunden';
      case 'monday':
        return status?.monday?.configured ? `Board ID: ${status.monday.board_id}` : 'Nicht konfiguriert';
      case 'smtp':
        return status?.smtp?.configured ? `Host: ${status.smtp.host}` : 'Nicht konfiguriert';
      default:
        return 'Unbekannt';
    }
  };

  return (
    <Layout>
      <div className=" animate-slide-in">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Oswald'] text-3xl md:text-4xl font-bold text-foreground uppercase tracking-tight">
              System Status
            </h1>
            <p className="text-muted-foreground mt-1">
              Überwachung aller Dienste
            </p>
          </div>
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            variant="outline"
            className="rounded-none border-border"
            data-testid="refresh-status-btn"
          >
            <RefreshCw className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} size={18} />
            Aktualisieren
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-card border border-border p-6">
                <div className="h-6 w-32 skeleton mb-4" />
                <div className="h-4 w-48 skeleton" />
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* Services Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* MongoDB */}
              <div className="bg-card border border-border p-6 card-hover" data-testid="status-mongodb">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-500/10 flex items-center justify-center">
                      <Database className="text-green-500" size={20} />
                    </div>
                    <h3 className="font-['Oswald'] text-lg font-bold text-foreground uppercase">MongoDB</h3>
                  </div>
                  {getStatusIcon(getDbStatus())}
                </div>
                <p className={`text-sm ${getStatusColor(getDbStatus())}`}>
                  {getStatusMessage('mongodb')}
                </p>
              </div>

              {/* Monday.com */}
              <div className="bg-card border border-border p-6 card-hover" data-testid="status-monday">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/10 flex items-center justify-center">
                      <Cloud className="text-blue-500" size={20} />
                    </div>
                    <h3 className="font-['Oswald'] text-lg font-bold text-foreground uppercase">Monday.com</h3>
                  </div>
                  {getStatusIcon(getMondayStatus())}
                </div>
                <p className={`text-sm ${getStatusColor(getMondayStatus())}`}>
                  {getStatusMessage('monday')}
                </p>
              </div>

              {/* SMTP */}
              <div className="bg-card border border-border p-6 card-hover" data-testid="status-smtp">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-purple-500/10 flex items-center justify-center">
                      <Mail className="text-purple-500" size={20} />
                    </div>
                    <h3 className="font-['Oswald'] text-lg font-bold text-foreground uppercase">SMTP</h3>
                  </div>
                  {getStatusIcon(getSmtpStatus())}
                </div>
                <p className={`text-sm ${getStatusColor(getSmtpStatus())}`}>
                  {getStatusMessage('smtp')}
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="stats-card">
                <div className="value">{status?.members?.total || 0}</div>
                <div className="label">Mitglieder Gesamt</div>
              </div>
              <div className="stats-card">
                <div className="value">{status?.members?.admins || 0}</div>
                <div className="label">Administratoren</div>
              </div>
            </div>

            {/* Email Test */}
            <div className="bg-card border border-border p-6">
              <h2 className="font-['Oswald'] text-xl font-bold text-foreground uppercase tracking-tight mb-6 flex items-center gap-3">
                <Mail size={24} />
                E-Mail Test
              </h2>
              
              <form onSubmit={handleSendTestEmail} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="test-email" className="text-xs uppercase tracking-wider text-muted-foreground">
                      Empfänger E-Mail
                    </Label>
                    <Input
                      id="test-email"
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      placeholder="test@example.com"
                      className="bg-secondary/50 border-border rounded-none"
                      required
                      data-testid="test-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="test-subject" className="text-xs uppercase tracking-wider text-muted-foreground">
                      Betreff
                    </Label>
                    <Input
                      id="test-subject"
                      type="text"
                      value={testSubject}
                      onChange={(e) => setTestSubject(e.target.value)}
                      className="bg-secondary/50 border-border rounded-none"
                      data-testid="test-subject-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="test-message" className="text-xs uppercase tracking-wider text-muted-foreground">
                    Nachricht
                  </Label>
                  <textarea
                    id="test-message"
                    value={testMessage}
                    onChange={(e) => setTestMessage(e.target.value)}
                    className="w-full h-24 bg-secondary/50 border border-border rounded-none p-3 text-foreground resize-none focus:border-primary focus:outline-none"
                    data-testid="test-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sendingEmail}
                  className="bg-primary hover:bg-red-700 text-foreground uppercase tracking-widest font-bold text-sm rounded-none btn-glow"
                  data-testid="send-test-email-btn"
                >
                  {sendingEmail ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="mr-2" size={18} />
                      Test E-Mail senden
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Last Update */}
            {status?.timestamp && (
              <p className="text-xs text-muted-foreground mt-4 text-right">
                Letzte Aktualisierung: {new Date(status.timestamp).toLocaleString('de-CH')}
              </p>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}
